import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, ArrowLeft,  Clock, Bell } from 'lucide-react';

interface ComingSoonProps {
  /** Feature name, e.g. "Escrow". */
  feature?: string;
  title?: string;
  description?: string;
  /** Free-form ETA, e.g. "Q3 2026". */
  eta?: string;
}

const ComingSoon: React.FC<ComingSoonProps> = ({
  feature,
  title,
  description,
  eta = '',
}) => {
  const navigate = useNavigate();

  const heading = title ?? `${feature ?? 'This feature'} is coming soon`;
  const body =
    description ??
    'We are busy building this experience. It will be available here shortly — thanks for your patience.';

  return (
    <div className="relative flex min-h-[70vh] w-full items-center justify-center overflow-hidden px-6 py-16">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-emerald-50" />
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-indigo-200/40 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-emerald-200/40 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
      </div>

      <div className="w-full max-w-xl text-center">
        {/* Icon badge */}
        <div className="mx-auto mb-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-white ring-1 ring-black/5">
            <Bell className="h-8 w-8 text-slate-800" />
        </div>

        {eta && (
          <div className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
            <Clock className="h-3.5 w-3.5" />
            {eta}
          </div>
        )}

        <h1 className="mb-4 flex items-center justify-center gap-2 text-xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          {heading}
        </h1>

        <p className="mx-auto mb-10 max-w-md text-base leading-relaxed text-gray-600">
          {body}
        </p>

        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-gray-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to dashboard
          </button>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Go back
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComingSoon;
